import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import AISparklesAnimated from '@srm/ui/ai-sparkles-animated';

interface ChatAITypingStatusProps {
    assistantName: string;
    typingStatusMessages?: string[];
}

const TYPING_STATUS_ROTATION_MS = 4000;
const DEFAULT_TYPING_STATUS_MESSAGES = ['Подбираю ответ...'];

const shuffleMessages = (messages: string[]): string[] => {
    const shuffled = [...messages];

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
    }

    return shuffled;
};

const ChatAITypingStatus: React.FC<ChatAITypingStatusProps> = ({ assistantName, typingStatusMessages }) => {
    const [statusMessages] = useState<string[]>(() => {
        const sourceMessages =
            typingStatusMessages && typingStatusMessages.length > 0
                ? typingStatusMessages
                : DEFAULT_TYPING_STATUS_MESSAGES;

        return shuffleMessages(sourceMessages);
    });
    const [statusIndex, setStatusIndex] = useState<number>(0);
    const measureRef = useRef<HTMLSpanElement>(null);
    const [textWidth, setTextWidth] = useState<number>();
    const currentStatusMessage = statusMessages[statusIndex] ?? statusMessages[0] ?? DEFAULT_TYPING_STATUS_MESSAGES[0];

    useEffect(() => {
        if (statusMessages.length <= 1) {
            return;
        }

        const intervalId = window.setInterval(() => {
            setStatusIndex((prev) => (prev + 1) % statusMessages.length);
        }, TYPING_STATUS_ROTATION_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [statusMessages.length]);

    useLayoutEffect(() => {
        if (!measureRef.current) {
            return;
        }

        setTextWidth(measureRef.current.getBoundingClientRect().width);
    }, [statusIndex, statusMessages]);

    return (
        <div className="chat-ai-typing-status" aria-live="polite">
            <AISparklesAnimated className="chat-ai-typing-status__sparkles" size={28} />
            <span className="chat-ai-typing-status__assistant">{assistantName}</span>
            <span className="chat-ai-typing-status__divider">·</span>
            <span className="chat-ai-typing-status__text-wrap" style={{ width: textWidth }}>
                <AnimatePresence initial={false} mode="wait">
                    <motion.span
                        key={statusIndex}
                        className="chat-ai-typing-status__text"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.3 }}
                    >
                        {currentStatusMessage}
                    </motion.span>
                </AnimatePresence>
            </span>
            <span ref={measureRef} className="chat-ai-typing-status__text-measure">
                {currentStatusMessage}
            </span>
        </div>
    );
};

export default ChatAITypingStatus;
